import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { spacesService } from '../services/spacesService';
import { Space, SpaceParticipant, SpaceMessage, CreateSpaceRequest } from '../types';

// 🎙️ Hook pour la gestion des Spaces
export const useSpaces = () => {
  const { user } = useAuth();
  
  // États
  const [activeSpaces, setActiveSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les Spaces actifs
  useEffect(() => {
    if (!user?.walletAddress) {
      setActiveSpaces([]);
      setLoading(false);
      return;
    }

    const loadActiveSpaces = async () => {
      setLoading(true);
      try {
        // Pour l'instant, on peut juste mettre un tableau vide
        // La méthode getActiveSpaces sera ajoutée plus tard
        setActiveSpaces([]);
      } catch (err) {
        setError('Erreur lors du chargement des Spaces');
        console.error('Erreur load Spaces:', err);
      } finally {
        setLoading(false);
      }
    };

    loadActiveSpaces();
  }, [user?.walletAddress]);

  const createSpace = useCallback(async (data: CreateSpaceRequest): Promise<string | null> => {
    if (!user?.walletAddress) return null;

    try {
      setError(null);
      const spaceId = await spacesService.createSpace(user.walletAddress, data);
      return spaceId;
    } catch (err) {
      setError('Erreur lors de la création du Space');
      console.error('Erreur create Space:', err);
      return null;
    }
  }, [user?.walletAddress]);

  const joinSpace = useCallback(async (spaceId: string): Promise<boolean> => {
    if (!user?.walletAddress) return false;

    try {
      setError(null);
      await spacesService.joinSpace(spaceId, user.walletAddress);
      return true;
    } catch (err) {
      setError('Erreur lors de la connexion au Space');
      console.error('Erreur join Space:', err);
      return false;
    }
  }, [user?.walletAddress]);

  const leaveSpace = useCallback(async (spaceId: string): Promise<boolean> => {
    if (!user?.walletAddress) return false;

    try {
      setError(null);
      await spacesService.leaveSpace(spaceId, user.walletAddress);
      return true;
    } catch (err) {
      setError('Erreur lors de la déconnexion du Space');
      console.error('Erreur leave Space:', err);
      return false;
    }
  }, [user?.walletAddress]);

  const startSpace = useCallback(async (spaceId: string): Promise<boolean> => {
    if (!user?.walletAddress) return false;

    try {
      setError(null);
      await spacesService.startSpace(spaceId, user.walletAddress);
      return true;
    } catch (err) {
      setError('Erreur lors du démarrage du Space');
      console.error('Erreur start Space:', err);
      return false;
    }
  }, [user?.walletAddress]);

  const endSpace = useCallback(async (spaceId: string): Promise<boolean> => {
    if (!user?.walletAddress) return false;

    try {
      setError(null);
      await spacesService.endSpace(spaceId, user.walletAddress);
      return true;
    } catch (err) {
      setError('Erreur lors de l\'arrêt du Space');
      console.error('Erreur end Space:', err);
      return false;
    }
  }, [user?.walletAddress]);

  return {
    // États
    activeSpaces,
    loading,
    error,

    // Actions
    createSpace,
    joinSpace,
    leaveSpace,
    startSpace,
    endSpace
  };
};

// 🎙️ Hook pour un Space spécifique
export const useSpace = (spaceId: string | null) => {
  const { user } = useAuth();
  
  // États
  const [space, setSpace] = useState<Space | null>(null);
  const [participants, setParticipants] = useState<SpaceParticipant[]>([]);
  const [messages, setMessages] = useState<SpaceMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les données du Space
  useEffect(() => {
    if (!spaceId || !user?.walletAddress) {
      setSpace(null);
      setParticipants([]);
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // S'abonner au Space
    const unsubscribeSpace = spacesService.subscribeToSpace(spaceId, (spaceData) => {
      setSpace(spaceData);
      setLoading(false);
    });

    // S'abonner aux participants
    const unsubscribeParticipants = spacesService.subscribeToSpaceParticipants(spaceId, (participantsData) => {
      setParticipants(participantsData);
    });

    // S'abonner aux messages
    const unsubscribeMessages = spacesService.subscribeToSpaceMessages(spaceId, (messagesData) => {
      setMessages(messagesData);
    });

    return () => {
      unsubscribeSpace();
      unsubscribeParticipants();
      unsubscribeMessages();
    };
  }, [spaceId, user?.walletAddress]);

  // Actions
  const sendMessage = useCallback(async (content: string): Promise<boolean> => {
    if (!spaceId || !user?.walletAddress || !content.trim()) return false;

    try {
      setError(null);
      await spacesService.sendSpaceMessage(spaceId, user.walletAddress, content.trim());
      return true;
    } catch (err) {
      setError('Erreur lors de l\'envoi du message');
      console.error('Erreur envoi message Space:', err);
      return false;
    }
  }, [spaceId, user?.walletAddress]);

  // Informations calculées
  const isUserInSpace = space?.participants.includes(user?.walletAddress || '') || false;
  const isUserHost = space?.hostId === user?.walletAddress;
  const isUserSpeaker = space?.speakers.includes(user?.walletAddress || '') || false;
  const currentParticipantCount = space?.participants.length || 0;

  return {
    // États
    space,
    participants,
    messages,
    loading,
    error,

    // Informations calculées
    isUserInSpace,
    isUserHost,
    isUserSpeaker,
    currentParticipantCount,

    // Actions
    sendMessage
  };
};

// 🎙️ Hook pour les Spaces liés à un post
export const usePostSpaces = (postId: string | null) => {
  const { user } = useAuth();
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!postId || !user?.walletAddress) {
      setSpaces([]);
      setLoading(false);
      return;
    }

    const loadSpaces = async () => {
      setLoading(true);
      try {
        // Petit délai pour s'assurer que l'auth Firebase est synchronisée
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const postSpaces = await spacesService.getSpacesByPost(postId);
        setSpaces(postSpaces);
      } catch (error) {
        console.error('Erreur chargement Spaces du post:', error);
        
        // Si c'est une erreur de permissions, on met un tableau vide
        // plutôt que de planter l'interface
        if (error instanceof Error && error.message.includes('permissions')) {
          console.warn('⚠️ Permissions manquantes pour charger les Spaces. Vérifiez les indexes Firebase.');
        }
        
        setSpaces([]);
      } finally {
        setLoading(false);
      }
    };

    loadSpaces();
  }, [postId, user?.walletAddress]);

  const activeSpace = spaces.find((space: Space) => space.isLive) || null;
  const hasActiveSpace = !!activeSpace;

  const createSpace = useCallback(async (data: CreateSpaceRequest): Promise<string | null> => {
    if (!user?.walletAddress || !postId) return null;

    try {
      // Vérification supplémentaire que l'utilisateur est bien authentifié
      console.log('🎙️ Création Space pour:', { postId, userWallet: user.walletAddress });
      
      const spaceId = await spacesService.createSpace(user.walletAddress, {
        ...data,
        postId: postId
      });
      
      console.log('✅ Space créé avec ID:', spaceId);
      
      // Recharger les spaces après création
      const postSpaces = await spacesService.getSpacesByPost(postId);
      setSpaces(postSpaces);
      
      return spaceId;
    } catch (error) {
      console.error('❌ Erreur lors de la création du Space:', error);
      
      // Messages d'aide plus précis
      if (error instanceof Error) {
        if (error.message.includes('Missing or insufficient permissions')) {
          console.warn('⚠️ Problème de permissions Firebase. Vérifiez que les indexes sont prêts et les règles déployées.');
        } else if (error.message.includes('network')) {
          console.warn('⚠️ Problème de réseau. Réessayez dans quelques secondes.');
        }
      }
      
      return null;
    }
  }, [user?.walletAddress, postId]);

  const joinSpace = useCallback(async (spaceId: string): Promise<boolean> => {
    if (!user?.walletAddress) return false;

    try {
      await spacesService.joinSpace(spaceId, user.walletAddress);
      // Mise à jour locale
      setSpaces((prev: Space[]) => prev.map((space: Space) => 
        space.id === spaceId 
          ? { ...space, participants: [...space.participants, user.walletAddress] }
          : space
      ));
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'adhésion au Space:', error);
      return false;
    }
  }, [user?.walletAddress]);

  const isUserInSpace = useCallback((spaceId: string): boolean => {
    if (!user?.walletAddress) return false;
    const space = spaces.find((s: Space) => s.id === spaceId);
    return space ? space.participants.includes(user.walletAddress) : false;
  }, [user?.walletAddress, spaces]);

  return {
    spaces,
    activeSpace,
    hasActiveSpace,
    loading,
    createSpace,
    joinSpace,
    isUserInSpace
  };
};
